import { computed, effect, inject, Injectable, Signal, untracked } from '@angular/core';
import { OnlinePlayOnlineGameService } from '@core/online-play/services/online-play-online-game.service';
import { OnlinePlayUserService } from '@core/online-play/services/online-play-user.service';
import { CameraStreamApiService } from '@dc-api/camera-stream.api.service';
import { JanusPublishData } from '@dc-core/dc-backend/dc-classes';
import { AlertService } from '@dc-core/dc-services/alert.service';
import { CAMERA_TYPE } from '@dc-core/dc-services/camera/camera.models';
import {
    ACTIVATE_CAMERA_DIALOG_ID,
    ActivateCameraDialogComponent,
    ActivateCameraDialogResponse,
} from 'src/app/modules/camera/dialogs/activate-camera/activate-camera.dialog';
import { SmartDeviceService } from 'src/app/modules/smart-devices/services/smart-device.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { fullscreenDialogClasses } from 'src/app/shared/utils/dialog.utils';
import { environment } from 'src/environments/environment';

import { InGameCameraService } from '../../modules/games/services/in-game-camera.service';
import { UserMediaService } from '../user-media.service';
import { CurrentDeviceCameraReconnectService } from './current-device-camera-reconnect.service';
import { JanusEventsService, JanusEventType } from './janus-events.service';
import { JanusLoggingService } from './janus-logging.service';
import { JanusPublisherService } from './janus-publisher.service';
import { JanusStateService } from './janus-state.service';
import { JanusSubscriberService } from './janus-subscriber.service';
import { JanusUserMediaService } from './janus-user-media.service';

@Injectable({
    providedIn: 'root',
})
export class JanusEventsListenerService {
    private modalService: ModalService = inject(ModalService);
    private alertService: AlertService = inject(AlertService);
    private janusStateService: JanusStateService = inject(JanusStateService);
    private janusPublisherService: JanusPublisherService = inject(JanusPublisherService);
    private janusSubscriberService: JanusSubscriberService = inject(JanusSubscriberService);
    private janusLoggingService: JanusLoggingService = inject(JanusLoggingService);
    private janusUserMediaService: JanusUserMediaService = inject(JanusUserMediaService);
    private janusEventsService: JanusEventsService = inject(JanusEventsService);
    private userMediaService: UserMediaService = inject(UserMediaService);
    private currentDeviceCameraReconnectService: CurrentDeviceCameraReconnectService = inject(
        CurrentDeviceCameraReconnectService
    );
    private inGameCameraService: InGameCameraService = inject(InGameCameraService);
    private smartDeviceService: SmartDeviceService = inject(SmartDeviceService);
    private cameraStreamApiService: CameraStreamApiService = inject(CameraStreamApiService);
    private onlinePlayOnlineGameService: OnlinePlayOnlineGameService = inject(OnlinePlayOnlineGameService);
    private onlinePlayUserService: OnlinePlayUserService = inject(OnlinePlayUserService);

    private insideOnlineLayout: Signal<boolean> = computed(() => this.onlinePlayOnlineGameService.insideOnlineLayout());

    private reconnectCameraDialog: HTMLIonModalElement = null;
    private checkReconnectDialogTimeout: ReturnType<typeof setTimeout> | null = null;

    private camDisconnected: boolean = false;
    private notifiedPlayerNotEnabled: boolean = false;
    private canRefreshPublishedVideoConstraints = false;

    constructor() {
        effect(() => {
            // Apply video constraints after stream has changed by itself
            const hasStreams = this.janusStateService.videoUserMedia().videoStreams.hasStreams;
            const isPublishing = untracked(this.janusStateService.videoPublisher).isPublishing;

            if (!hasStreams || !isPublishing) {
                this.canRefreshPublishedVideoConstraints = false;
                return;
            }

            if (!this.canRefreshPublishedVideoConstraints) {
                this.canRefreshPublishedVideoConstraints = true;
                return;
            }

            untracked(() => {
                const isZoomSupported = this.userMediaService.isZoomSupported();
                if (isZoomSupported) this.userMediaService.applyVideoConstraints(this.janusStateService.videoUserMedia);
            });
        });

        effect(() => {
            // Reset the player not enabled alert when not in online layout anymore
            if (!this.insideOnlineLayout()) {
                this.notifiedPlayerNotEnabled = false;
            }
        });
    }

    public initJanusEventsListener(): void {
        this.janusEventsService.events$.subscribe(async (event) => {
            this.janusLoggingService.log('********** Janus event', event.type);

            const janusServerHost = event.janusServerHost;

            switch (event.type) {
                case JanusEventType.ExternalConnectionLost: // Handle external connection lost
                case JanusEventType.OwnDeviceDisconnected:
                case JanusEventType.ResetExternalDevice:
                    // Handle own device disconnected
                    if (this.camDisconnected) {
                        break;
                    }

                    if (event.type === JanusEventType.OwnDeviceDisconnected && this.janusStateService.isExternal) {
                        break;
                    }

                    this.camDisconnected = true;

                    this.modalService
                        .createModal({
                            component: ActivateCameraDialogComponent,
                            id: ACTIVATE_CAMERA_DIALOG_ID,
                            backdropDismiss: false,
                            showBackdrop: true,
                            cssClass: fullscreenDialogClasses,
                        })
                        .then((elem) => {
                            if (this.checkReconnectDialogTimeout) clearTimeout(this.checkReconnectDialogTimeout);

                            this.checkReconnectDialogTimeout = setTimeout(() => {
                                this.checkReconnectDialogTimeout = null;

                                this.reconnectCameraDialog = elem;
                                this.reconnectCameraDialog.present().finally(() => {
                                    this.alertService.showPrompt(
                                        $localize`:@@YOUR_CAM_DISCONNECTED:Your camera is disconnected!`,
                                        'warning'
                                    );
                                });
                                this.reconnectCameraDialog.onDidDismiss().then((dialogRes) => {
                                    this.reconnectCameraDialog = null;

                                    const response: ActivateCameraDialogResponse = dialogRes.data;
                                    if (response && response.janusPublishData) {
                                        this.camDisconnected = false;
                                        this.alertService.createAlert({
                                            title: $localize`:@@CAMERA_IS_ACTIVATED:Your camera is activated for online games!`,
                                        });

                                        this.janusEventsService.emitEvent({
                                            type: JanusEventType.NewRoom,
                                            previousType: event.type,
                                            janusPublishData: response.janusPublishData,
                                        });
                                    }
                                });
                            }, 5000);
                        });
                    break;
                case JanusEventType.NewRoom:
                    if (event.janusPublishData?.camera_type === CAMERA_TYPE.EXTERNAL_DEVICE) {
                        this.clearReconnectCameraState();
                    }

                    this.onlinePlayUserService.updateJanusPublishData(event.janusPublishData ?? null);

                    if (this.janusStateService.isExternal && this.janusStateService.hasExternalCredentials()) {
                        let hasCameraStream = false;
                        try {
                            hasCameraStream = await this.updateCameraStream(event.janusPublishData);
                        } catch {
                            hasCameraStream = await this.generateCameraStream(event.janusPublishData);
                        }

                        if (hasCameraStream) {
                            this.janusStateService.videoUserMedia.update((userMedia) => ({
                                ...userMedia,
                                janusPublishData: event.janusPublishData,
                            }));
                        }
                    }
                    break;
                case JanusEventType.ResetCurrentDevice:
                    await this.currentDeviceCameraReconnectService.resetCurrentDevice(
                        event.type,
                        event.media,
                        event.dismissLoader
                    );
                    break;
                case JanusEventType.ResetSmartDevice:
                    void (async () => {
                        try {
                            await this.smartDeviceService.stopStreaming(event.smartDevice, true, false);
                            const streamingRes = await this.smartDeviceService.startStreaming(event.smartDevice, true);
                            await this.janusPublisherService.useExternalRoom(
                                streamingRes.janusPublishData,
                                CAMERA_TYPE.SMART_DEVICE
                            );

                            this.janusEventsService.emitEvent({
                                type: JanusEventType.NewRoom,
                                previousType: event.type,
                                janusPublishData: this.janusStateService.videoRoom(),
                            });
                        } catch (error) {
                            this.janusLoggingService.error('Error resetting smart device stream', error);
                        }
                    })();
                    break;
                case JanusEventType.PlayerCamNotEnabled:
                    //Don't show popup anymore, maybe in future we show something else on the page.
                    break;
                case JanusEventType.OtherUserStreamDisconnect:
                    // Handle other user device disconnected
                    break;
                case JanusEventType.NoRoomExists:
                    if (this.janusStateService.videoRoom().janus_server_host === janusServerHost) {
                        this.janusPublisherService.restartOwnStreamWhenPublishing();
                    }
                    break;
                case JanusEventType.OwnDeviceConnected:
                case JanusEventType.OwnExternalDeviceConnected:
                    this.clearReconnectCameraState();
                    break;
                case JanusEventType.ServerReconnected:
                case JanusEventType.ServerDestroyed:
                    if (environment.debug) {
                        console.log(
                            '********** Refreshing all streams',
                            janusServerHost,
                            this.janusStateService.videoRoom().janus_server_host
                        );
                    }

                    this.currentDeviceCameraReconnectService.reconnectCurrentDeviceAfterServerReconnect(
                        janusServerHost,
                        event.type
                    );
                    this.checkExternalDeviceReconnect(janusServerHost);
                    this.checkOnlineGameRooms(janusServerHost);
                    this.checkSmartDeviceReconnect(janusServerHost);
                    break;
                default:
                    // Handle unknown event
                    break;
            }
        });
    }

    private async generateCameraStream(janusPublishData: JanusPublishData): Promise<boolean> {
        const { janus_server_host, room_id, stream_id } = janusPublishData;

        try {
            const { data } = await this.cameraStreamApiService.storeCameraStreamByRoomNumber({
                janus_server_hostname: janus_server_host,
                room: room_id,
                stream_id: stream_id,
                virt_cam_id: null,
            });

            this.janusStateService.externalCode.set(data.code);
            this.janusStateService.externalPassword.set(data.password);

            return true;
        } catch {
            this.janusStateService.externalCode.set(null);
            this.janusStateService.externalPassword.set(null);

            return false;
        }
    }

    private async updateCameraStream(janusPublishData: JanusPublishData): Promise<boolean> {
        const { janus_server_host, room_id, stream_id } = janusPublishData;

        await this.cameraStreamApiService.updateCameraStreamByCodeAndPassword({
            code: this.janusStateService.externalCode(),
            password: this.janusStateService.externalPassword(),
            janus_server_hostname: janus_server_host,
            room: room_id,
            stream_id: stream_id,
        });

        return true;
    }

    private checkExternalDeviceReconnect(janusServerHost: string): void {
        const videoRoom = this.janusStateService.videoRoom();

        if (janusServerHost === videoRoom.janus_server_host && this.janusStateService.usingExternal) {
            this.janusStateService.videoUserMedia.update((userMedia) => ({
                ...this.janusUserMediaService.cleanupUserMedia(userMedia, true, true),
            }));

            this.janusStateService.detachAllExternalPublisherHandles().finally(() => {
                if (!this.janusStateService.usingExternal || this.janusStateService.videoRoom() !== videoRoom) return;

                this.janusPublisherService
                    .useExternalRoom(videoRoom, null)
                    .catch((error) =>
                        this.janusLoggingService.error('Error rejoining external room after reconnect', error)
                    );
            });
        }
    }

    private checkOnlineGameRooms(janusServerHost: string): void {
        const onlineGameUsers = this.onlinePlayOnlineGameService
            .currentOnlineGame()
            ?.teams?.flatMap((team) => team.users)
            ?.filter((user) => user?.janus_video_data?.janus_server_host === janusServerHost);

        onlineGameUsers?.forEach((user) => {
            this.inGameCameraService
                .joinJanusRoom(user.janus_video_data, user, null, {
                    disableSetOwnUserMedia: true,
                    // On Janus server reconnect we must force rejoin even if local streams still look active.
                    skipSameJanusCheck: true,
                })
                .catch((error) => this.janusLoggingService.error('Error rejoining online game Janus room', error));
        });
    }

    private checkSmartDeviceReconnect(janusServerHost: string): void {
        const smartDeviceJanusPublishData = this.smartDeviceService.userMedia().janusPublishData;

        const smartDeviceRoomId = smartDeviceJanusPublishData?.room_id;
        const smartDeviceHostname = smartDeviceJanusPublishData?.janus_server_host;
        const smartDeviceStreamId = smartDeviceJanusPublishData?.stream_id;

        if (smartDeviceRoomId && janusServerHost === smartDeviceHostname) {
            this.smartDeviceService.userMedia.update((userMedia) => ({
                ...this.janusUserMediaService.cleanupUserMedia(userMedia, true, true),
            }));

            this.janusStateService
                .leaveRoomAndDetachAllHandles(smartDeviceRoomId)
                .catch((error) =>
                    this.janusLoggingService.error(`Error leaving smart device room #${smartDeviceRoomId}`, error)
                )
                .finally(() => {
                    this.janusSubscriberService
                        .spectateRoom(
                            {
                                janus_server_host: smartDeviceHostname,
                                room_id: smartDeviceRoomId,
                                stream_id: smartDeviceStreamId,
                                camera_type: CAMERA_TYPE.SMART_DEVICE,
                            },
                            'video',
                            null,
                            this.smartDeviceService.userMedia,
                            true,
                            false
                        )
                        .catch((error) =>
                            this.janusLoggingService.error(
                                `Error resubscribing smart device stream for room #${smartDeviceRoomId}`,
                                error
                            )
                        );
                });
        }
    }

    private clearReconnectCameraState(): void {
        this.camDisconnected = false;

        if (this.checkReconnectDialogTimeout) {
            clearTimeout(this.checkReconnectDialogTimeout);
            this.checkReconnectDialogTimeout = null;
        }

        if (this.reconnectCameraDialog) {
            this.alertService.clearAlerts();

            try {
                this.reconnectCameraDialog.dismiss();
            } finally {
                this.reconnectCameraDialog = null;
            }
        }
    }
}
